import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "src/api";
import {
  BannerCarousel,
  BannerModel,
  BannerProject,
  BannerWriteRequest,
} from "src/api/generated";

export interface BannersListParams {
  page: number;
  pageSize: number;
  placement?: "profile" | "calendar" | "prepayment";
  platform?: "android" | "ios" | "web";
  status?: "active" | "inactive" | "scheduled" | "expired";
  search?: string;
  sort?: string;
}

// Фабрика query-ключей: инвалидация bannerKeys.all сбрасывает и списки, и meta
export const bannerKeys = {
  all: ["banners"] as const,
  lists: () => [...bannerKeys.all, "list"] as const,
  list: (params: BannersListParams) => [...bannerKeys.lists(), params] as const,
  meta: () => [...bannerKeys.all, "meta"] as const,
  carousel: () => [...bannerKeys.all, "carousel"] as const,
  projects: (search: string) =>
    [...bannerKeys.all, "projects", search] as const,
};

// Размер страницы поисковой выдачи справочника проектов
// (adminBannerProjects: @min 1 @max 100) — берём максимум, чтобы реже ходить
export const BANNER_PROJECTS_LIMIT = 100;

export const useBannersList = (params: BannersListParams) =>
  useQuery({
    queryKey: bannerKeys.list(params),
    queryFn: () =>
      api.admin_panel.adminBannerList({
        page: params.page,
        pageSize: params.pageSize,
        placement: params.placement,
        platform: params.platform,
        status: params.status,
        search: params.search || undefined,
        sort: params.sort,
      }),
  });

export const useBannerMeta = () =>
  useQuery({
    queryKey: bannerKeys.meta(),
    queryFn: () => api.admin_panel.adminBannerMeta(),
    staleTime: 5 * 60 * 1000,
  });


export const useBannerProjectsSearch = (search: string) =>
  useInfiniteQuery({
    queryKey: bannerKeys.projects(search),
    queryFn: ({pageParam, signal}) =>
      api.admin_panel.adminBannerProjects(
        {
          search: search || undefined,
          limit: BANNER_PROJECTS_LIMIT,
          offset: pageParam,
        },
        {signal},
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _pages, lastPageParam) => {
      const loaded = lastPageParam + (lastPage.items?.length ?? 0);
      // пустая страница — страховка от бесконечного цикла, если total завышен
      if (!lastPage.items?.length) {
        return undefined;
      }
      return loaded < (lastPage.total ?? 0) ? loaded : undefined;
    },
    select: (data): {items: BannerProject[]; total: number} => ({
      items: data.pages.flatMap((page) => page.items ?? []),
      total: data.pages[0]?.total ?? 0,
    }),
    staleTime: 5 * 60 * 1000,
    // пока грузится новая выдача, показываем предыдущую — список не мигает
    placeholderData: keepPreviousData,
  });

export const useCarouselSettings = (enabled: boolean) =>
  useQuery({
    queryKey: bannerKeys.carousel(),
    queryFn: () => api.admin_panel.adminBannerCarouselGet(),
    enabled,
  });

const useInvalidateBanners = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: bannerKeys.all });
};

export const useCreateBanner = () => {
  const invalidate = useInvalidateBanners();
  return useMutation({
    mutationFn: (body: BannerWriteRequest) =>
      api.admin_panel.adminBannerCreate(body),
    onSuccess: invalidate,
  });
};

export const useUpdateBanner = () => {
  const invalidate = useInvalidateBanners();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: BannerWriteRequest }) =>
      api.admin_panel.adminBannerUpdate(id, body),
    onSuccess: invalidate,
  });
};

export const useDeleteBanner = () => {
  const invalidate = useInvalidateBanners();
  return useMutation({
    mutationFn: (id: string) => api.admin_panel.adminBannerDelete(id),
    onSuccess: invalidate,
  });
};

export const useToggleBanner = () => {
  const invalidate = useInvalidateBanners();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.admin_panel.adminBannerToggle(id, { isActive }),
    onSuccess: invalidate,
  });
};

export const useUpdateBannerPriority = () => {
  const invalidate = useInvalidateBanners();
  return useMutation({
    mutationFn: ({ id, priority }: { id: string; priority: number }) =>
      api.admin_panel.adminBannerPriority(id, { priority }),
    onSuccess: invalidate,
  });
};

export const useUploadBannerImage = () =>
  useMutation({
    mutationFn: (file: File) =>
      api.admin_panel.adminBannerUpload({ file }),
  });

export const useUpdateCarouselSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: BannerCarousel) =>
      api.admin_panel.adminBannerCarouselPut(body),
    onSuccess: (data) =>
      queryClient.setQueryData(bannerKeys.carousel(), data),
  });
};

export type { BannerModel, BannerProject };
